import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { NumberField } from '@/components/ui/number-field';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import type { TimesheetDeadlineSettings } from '@/types/timesheetSettings';

interface ApprovalReminderSettingsProps {
  settings: TimesheetDeadlineSettings;
  onChange: (settings: TimesheetDeadlineSettings, isValid: boolean) => void;
}

// ---------------------------------------------------------------------------
// Setting card — matches Submission Reminders card pattern
// ---------------------------------------------------------------------------

interface SettingCardProps {
  id: string;
  label: string;
  description: string;
  control: React.ReactNode;
  onClick?: () => void;
}

function SettingCard({ id, label, description, control, onClick }: SettingCardProps) {
  return (
    <div
      className={`flex items-center justify-between gap-4 p-4 rounded-lg border bg-background transition-colors
        ${onClick ? 'cursor-pointer select-none hover:bg-accent/50' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <Label
          htmlFor={id}
          className={`text-sm font-medium leading-snug ${onClick ? 'cursor-pointer select-none' : ''}`}
        >
          {label}
        </Label>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
        {control}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ApprovalReminderSettings: React.FC<ApprovalReminderSettingsProps> = ({
  settings,
  onChange,
}) => {
  const enableApprovalReminders = settings.enableApprovalReminders ?? false;
  const approvalReminderDaysThreshold = settings.approvalReminderDaysThreshold ?? 2;

  const isThresholdValid =
    Number.isInteger(approvalReminderDaysThreshold) &&
    approvalReminderDaysThreshold >= 1 &&
    approvalReminderDaysThreshold <= 14;

  const handleToggleChange = (checked: boolean) => {
    const newSettings: TimesheetDeadlineSettings = {
      ...settings,
      enableApprovalReminders: checked,
    };
    const valid = !checked || isThresholdValid;
    onChange(newSettings, valid);
  };

  const handleThresholdChange = (value: number) => {
    const newSettings: TimesheetDeadlineSettings = {
      ...settings,
      approvalReminderDaysThreshold: value,
    };
    const valid = Number.isInteger(value) && value >= 1 && value <= 14;
    onChange(newSettings, valid);
  };

  const showThresholdInput = enableApprovalReminders;
  const showThresholdError = showThresholdInput && !isThresholdValid;

  return (
    <Accordion type="multiple" defaultValue={['approval-reminders']} className="space-y-4">
      <AccordionItem value="approval-reminders" className="border rounded-lg">
        <AccordionTrigger className="px-4 hover:no-underline">
          <div className="flex items-center gap-3 text-left">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <CheckCircle className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-semibold">Approval Reminders</p>
              <p className="text-xs text-muted-foreground font-normal mt-0.5">
                Configure reminders for approvers with outstanding timesheets
              </p>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pt-2 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <SettingCard
              id="enableApprovalReminders"
              label="Approval Reminder Notifications"
              description="Enable feed notifications to approvers with outstanding timesheets"
              control={
                <Checkbox
                  id="enableApprovalReminders"
                  checked={enableApprovalReminders}
                  onCheckedChange={(v) => handleToggleChange(!!v)}
                />
              }
              onClick={() => handleToggleChange(!enableApprovalReminders)}
            />
          </div>

          {/* Threshold input — only shown when checkbox is on */}
          {showThresholdInput && (
            <div className="pl-4 border-l-2 border-muted space-y-2 mt-4">
              <Label htmlFor="approvalReminderDaysThreshold">
                Send reminder after a timesheet has been waiting for
              </Label>
              <div className="flex gap-2">
                <NumberField
                  id="approvalReminderDaysThreshold"
                  min={1}
                  max={14}
                  clampOnBlur={false}
                  value={approvalReminderDaysThreshold}
                  onChange={handleThresholdChange}
                  className={`h-10 w-20${showThresholdError ? ' border-destructive focus-visible:ring-destructive' : ''}`}
                  aria-describedby={showThresholdError ? 'approval-threshold-error' : 'approval-threshold-hint'}
                  aria-invalid={showThresholdError}
                />
                <div className="flex items-center px-3 text-sm text-muted-foreground whitespace-nowrap">
                  days
                </div>
              </div>

              {showThresholdError ? (
                <p id="approval-threshold-error" className="text-xs text-destructive">
                  Please enter a value between 1 and 14.
                </p>
              ) : (
                <p id="approval-threshold-hint" className="text-xs text-muted-foreground">
                  How many days a timesheet can be waiting before sending a reminder
                </p>
              )}

              <p className="text-sm text-muted-foreground">
                Reminders are sent to the assigned approver only.
              </p>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
